import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { Button, Paper, Text, Modal } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconTag } from "@tabler/icons-react";

import { motion } from "framer-motion";

import { AppName, BrandImages, Colors } from "../../Lib/Data";
import {
  DefaultSuccessNotification,
  getDiscountedValue,
  getFinancialValueFromNumeric,
} from "../../Lib/Methods";

import {
  googleOneStorageProps,
  iCloudStorageProps,
  PossiblePeriods,
  PossibleServices,
  UserCartProps,
} from "../../Lib/Types";

import MyCartSection from "../../Components/Cart";
import { AppContext } from "../Wrapper";
import { Endpoints } from "../../Lib/Endpoints";
import { PerformRequest } from "../../Lib/usePerformRequest";

import MegaLoader from "../../Components/MegaLoader";
import "./styles.scss";

const Services: React.FC = () => {
  const context = useContext(AppContext);
  const theme = context?.theme ?? "light";
  const location = useLocation();

  document.title = `Services | ${AppName}`;

  const [selectedServices, setSelectedServices] = useState<PossibleServices[]>(
    []
  );

  const [selectedPeriod, setSelectedPeriod] =
    useState<PossiblePeriods>("1 Year");
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [totalSaved, setTotalSaved] = useState<number>(0);

  const [selectedICloudOption, setSelectedICloudOption] = useState<
    number | null
  >(null);
  const [selectedGoogleOneOption, setSelectedGoogleOneOption] = useState<
    number | null
  >(null);

  const [iCloudStorage, setICloudStorage] = useState<iCloudStorageProps | null>(
    null
  );
  const [googleOneStorage, setGoogleOneStorage] =
    useState<googleOneStorageProps | null>(null);

  useEffect(() => {
    if (context && context.pricing && context.pricing.possiblePeriodsCost) {
      let price = context.pricing.pricing
        .map((s) => {
          return (
            !["iCloud - Apple Music", "Google One"].includes(s.service) && 
            selectedServices.includes(s.service)
          ) ? s.price : 0;
        })
        .reduce((prev, current) => prev + current);
      if (selectedServices.includes("iCloud - Apple Music") && iCloudStorage) {
        price +=
          context.pricing.iCloudAppleMusicPricing.find(
            (i) => i.storage === iCloudStorage
          )?.price ?? 0;
      }
      if (selectedServices.includes("Google One") && googleOneStorage) {
        price +=
          context.pricing.googleOneStoragePricing.find(
            (i) => i.storage === googleOneStorage
          )?.price ?? 0;
      }
      // UPDATE PRICE DEPENDING ON DURATION CHOSEN
      price =
        price *
        (context.pricing?.possiblePeriodsCost?.find(
          (p) => p.period === selectedPeriod
        )?.factor ?? 1);

      // CHECK FOR DISCOUNT DEPENDING ON DURATION
      const durationDiscount = context.pricing.durationDiscounts?.find(
        (d) => d.period === selectedPeriod
      )?.discount;
      let discounted = getDiscountedValue(price, durationDiscount ?? 0);
      setTotalAmount(discounted);
      setTotalSaved(price - discounted);
    }
  }, [selectedServices, selectedPeriod, context?.pricing]);

  useEffect(() => {
    let pendingServices = localStorage.getItem("pending_services")
      ? JSON.parse(localStorage.getItem("pending_services") as string)
      : undefined;
    let pendingGoogleOneStorage = localStorage.getItem(
      "pending_google_one_storage"
    )
      ? JSON.parse(localStorage.getItem("pending_google_one_storage") as string)
      : undefined;
    let pendingICloudStorage = localStorage.getItem("pending_iCloud_storage")
      ? JSON.parse(localStorage.getItem("pending_iCloud_storage") as string)
      : undefined;

    const pendingPeriod = localStorage.getItem("pending_period");
    if (pendingServices && pendingServices.length > 0 && pendingPeriod) {
      setSelectedServices(pendingServices);
      setSelectedPeriod(pendingPeriod as any);
      localStorage.removeItem("pending_services");
      localStorage.removeItem("pending_period");
    }
    if (pendingGoogleOneStorage) {
      setGoogleOneStorage(pendingGoogleOneStorage);
      localStorage.removeItem("pending_google_one_storage");
    }
    if (pendingICloudStorage) {
      setICloudStorage(pendingICloudStorage);
      localStorage.removeItem("pending_iCloud_storage");
    }
  }, []);

  // Load cart from backend or guest storage when there's no pending cart from auth redirect
  useEffect(() => {
    const loadCart = async () => {
      // if user already has selections (e.g. from pending_*), don't override
      if (selectedServices.length > 0) return;

      if (context?.profile) {
        const r = await PerformRequest<{
          data: UserCartProps;
        }>({
          method: "GET",
          route: Endpoints.GetUserCart(),
        });
        if (r && r.status === 200 && r.data?.data) {
          const cart = r.data.data;
          setSelectedServices(cart.services || []);
          setSelectedPeriod(cart.duration);
          if (cart.iCloudStorage) setICloudStorage(cart.iCloudStorage);
          if (cart.googleOneStorage)
            setGoogleOneStorage(cart.googleOneStorage);
        }
      } else {
        const guestCartRaw = localStorage.getItem("guest_cart");
        if (guestCartRaw) {
          try {
            const cart = JSON.parse(guestCartRaw) as UserCartProps;
            if (cart.services && cart.services.length > 0) {
              setSelectedServices(cart.services);
              setSelectedPeriod(cart.duration);
              if (cart.iCloudStorage) setICloudStorage(cart.iCloudStorage);
              if (cart.googleOneStorage)
                setGoogleOneStorage(cart.googleOneStorage);
            }
          } catch {
            // ignore bad data
          }
        }
      }
    };

    loadCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context?.profile]);

  // Persist cart whenever selection changes
  useEffect(() => {
    const hasSelection = selectedServices.length > 0;

    const payload: Partial<UserCartProps> = {
      services: selectedServices,
      duration: selectedPeriod,
      iCloudStorage: iCloudStorage || undefined,
      googleOneStorage: googleOneStorage || undefined,
    };

    // If nothing is selected, don't touch backend cart for logged-in users.
    // Guest carts are safe to clear from localStorage when empty.
    if (!hasSelection) {
      if (!context?.profile) {
        localStorage.removeItem("guest_cart");
      }
      return;
    }

    if (context?.profile) {
      PerformRequest({
        method: "PUT",
        route: Endpoints.PutUserCart(),
        data: payload,
      });
    } else {
      localStorage.setItem("guest_cart", JSON.stringify(payload));
    }
  }, [
    selectedServices,
    selectedPeriod,
    iCloudStorage,
    googleOneStorage,
    context?.profile,
  ]);

  const HandlePaymentSuccess = () => {
    setSelectedServices([]);
    setSelectedPeriod("1 Year");
    localStorage.removeItem("pending_services");
    localStorage.removeItem("pending_period");
    DefaultSuccessNotification("Payment successful!");
  };

  const isAppleMusicInBundle = selectedServices.includes(
    "iCloud - Apple Music"
  );
  const isGoogleOneInBundle = selectedServices.includes("Google One");
  const [
    isAppleMusicModalOpened,
    { open: OpenAppleMusicModal, close: CloseAppleMusicModal },
  ] = useDisclosure(false);
  const [
    isGoogleOneModalOpened,
    { open: OpenGoogleOneModal, close: CloseGoogleOneModal },
  ] = useDisclosure(false);

  const isAnyServiceModalOpen = isAppleMusicModalOpened || isGoogleOneModalOpened;

  useEffect(() => {
    if (!isAnyServiceModalOpen) {
      return;
    }

    // Push a new history state so that the first Back closes the modal
    window.history.pushState(
      { modal: "service-selection", path: location.pathname },
      "",
      window.location.href,
    );

    const handlePopState = (event: PopStateEvent) => {
      if (isAppleMusicModalOpened || isGoogleOneModalOpened) {
        event.preventDefault();
        closeAppleMusicModalIfOpen();
        closeGoogleOneModalIfOpen();
        // Restore the current URL so the user stays on the page
        window.history.pushState(
          { modal: "service-selection-closed", path: location.pathname },
          "",
          window.location.href,
        );
      }
    };

    const closeAppleMusicModalIfOpen = () => {
      if (isAppleMusicModalOpened) {
        CloseAppleMusicModal();
      }
    };

    const closeGoogleOneModalIfOpen = () => {
      if (isGoogleOneModalOpened) {
        CloseGoogleOneModal();
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
    // We intentionally depend only on the open-state flags and pathname
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnyServiceModalOpen, isAppleMusicModalOpened, isGoogleOneModalOpened, location.pathname]);
  return (
    <div className="services-page-container">
      {context?.isLoadingPricing ? (
        <MegaLoader />
      ) : (
        <div className="content-wrapper">
          <div className="main-content">
            <div className="header-section">
              <Text
                className="page-title"
                c={theme === "dark" ? Colors.White : Colors.BlackPrimary}
              >
                Explore Premium Services
              </Text>
              <Text
                className="page-subtitle"
                c={
                  theme === "dark"
                    ? Colors.WhiteBlueTransparent
                    : Colors.GraySecondary
                }
              >
                Select the subscriptions you want to bundle and save more.
              </Text>
            </div>

            <div className="services-grid">
              {context?.pricing?.iCloudAppleMusicPricing &&
                context.pricing.iCloudAppleMusicPricing.length > 0 && (
                  <motion.div
                    whileHover={{ y: -5 }}
                    transition={{ duration: 0.2 }}
                    className={`service-card glass-card ${theme} ${
                      isAppleMusicInBundle ? "selected" : ""
                    }`}
                  >
                    <div className="card-header">
                      <div className="icon-wrapper">
                        <img
                          src={BrandImages.retrieveImage(
                            "iCloud - Apple Music",
                            theme
                          )}
                          alt=""
                        />
                      </div>
                      <div className="price-tag">
                        <Text span fw={700} fz={18}>
                          $
                          {getFinancialValueFromNumeric(
                            context.pricing.iCloudAppleMusicPricing.find(
                              (i) => i.storage === iCloudStorage
                            )?.price ??
                              context.pricing.iCloudAppleMusicPricing[0].price
                          )}
                        </Text>
                      </div>
                    </div>
                    <div className="card-body">
                      <Text fw={600} fz={18} mb={4} className="service-name">
                        iCloud + Apple Music
                      </Text>
                      <Text fz={14} className="service-description">
                        Extend your iCloud storage. Includes Apple Music!
                      </Text>
                    </div>
                    <div className="card-footer">
                      <Button
                        onClick={() => OpenAppleMusicModal()}
                        className={`action-button ${
                          isAppleMusicInBundle ? "active" : ""
                        }`}
                        fullWidth
                        radius="md"
                        size="md"
                      >
                        {isAppleMusicInBundle ? "Manage" : "Add to Bundle"}
                      </Button>
                    </div>
                  </motion.div>
                )}

              {context?.pricing?.googleOneStoragePricing &&
                context.pricing.googleOneStoragePricing.length > 0 && (
                  <motion.div
                    whileHover={{ y: -5 }}
                    transition={{ duration: 0.2 }}
                    className={`service-card glass-card ${theme} ${
                      isGoogleOneInBundle ? "selected" : ""
                    }`}
                  >
                    <div className="card-header">
                      <div className="icon-wrapper">
                        <img
                          src={BrandImages.retrieveImage("Google One", theme)}
                          alt=""
                        />
                      </div>
                      <div className="price-tag">
                        <Text span fw={700} fz={18}>
                          $
                          {getFinancialValueFromNumeric(
                            context.pricing.googleOneStoragePricing.find(
                              (i) => i.storage === googleOneStorage
                            )?.price ??
                              context.pricing.googleOneStoragePricing[0].price
                          )}
                        </Text>
                      </div>
                    </div>
                    <div className="card-body">
                      <Text fw={600} fz={18} mb={4} className="service-name">
                        Google One
                      </Text>
                      <Text fz={14} className="service-description">
                        Extend your Google One storage.
                      </Text>
                    </div>
                    <div className="card-footer">
                      <Button
                        onClick={() => OpenGoogleOneModal()}
                        className={`action-button ${
                          isGoogleOneInBundle ? "active" : ""
                        }`}
                        fullWidth
                        radius="md"
                        size="md"
                      >
                        {isGoogleOneInBundle ? "Manage" : "Add to Bundle"}
                      </Button>
                    </div>
                  </motion.div>
                )}

              {context?.pricing?.pricing?.map((service, index) => {
                const isInBundle = selectedServices.includes(service.service);
                return (
                  <motion.div
                    key={service.service}
                    whileHover={{ y: -5 }}
                    transition={{ duration: 0.2 }}
                    className={`service-card glass-card ${theme} ${
                      isInBundle ? "selected" : ""
                    }`}
                  >
                    <div className="card-header">
                      <div className="icon-wrapper">
                        <img
                          src={BrandImages.retrieveImage(
                            service.service,
                            theme
                          )}
                          alt=""
                        />
                      </div>
                      <div className="price-tag">
                        <Text span fw={700} fz={18}>
                          ${getFinancialValueFromNumeric(service.price)}
                        </Text>
                      </div>
                    </div>
                    <div className="card-body">
                      <Text fw={600} fz={18} mb={4} className="service-name">
                        {service.service}
                      </Text>
                      <Text fz={14} className="service-description">
                        {service.description}
                      </Text>
                    </div>
                    <div className="card-footer">
                      <Button
                        onClick={() => {
                          setSelectedServices((prevServices) =>
                            isInBundle
                              ? prevServices.filter(
                                  (s) => s !== service.service
                                )
                              : [...prevServices, service.service]
                          );
                        }}
                        className={`action-button ${
                          isInBundle ? "active" : ""
                        }`}
                        fullWidth
                        radius="md"
                        size="md"
                      >
                        {isInBundle ? "Remove" : "Add to Bundle"}
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <div className="cart-sidebar-wrapper">
            <MyCartSection
              selectedServices={selectedServices}
              selectedPeriod={selectedPeriod}
              setSelectedPeriod={setSelectedPeriod}
              totalAmount={totalAmount}
              totalSaved={totalSaved}
              handlePaymentSuccess={HandlePaymentSuccess}
              iCloudStorage={iCloudStorage}
              googleOneStorage={googleOneStorage}
            />
          </div>
        </div>
      )}

      {/* Modals remain mostly the same structure, just wrapped/styled differently if needed. 
          The previous logic for modals is preserved below in the original return scope, 
          but we are replacing the main render logic. 
          Because replace_file_content replaces a block, I need to ensure the closing tags align.
      */}

      {/* Re-inserting modals here to ensure they are rendered */}
      <Modal
        opened={isAppleMusicModalOpened}
        onClose={CloseAppleMusicModal}
        size="lg"
        centered
        withCloseButton={false}
        className={`service-bundle-modal ${theme}`}
        overlayProps={{
          blur: 8,
        }}
        transitionProps={{ transition: "fade", duration: 200 }}
      >
        <div className="modal-content">
          <div className="header">
            <Text
              className="title"
              c={theme === "dark" ? Colors.White : Colors.BlackPrimary}
            >
              {isAppleMusicInBundle
                ? "Remove iCloud Storage"
                : "Choose Your iCloud Storage"}
            </Text>
            <Text
              className="subtitle"
              c={
                theme === "dark"
                  ? Colors.WhiteBlueTransparent
                  : Colors.BluePrimary
              }
            >
              {isAppleMusicInBundle
                ? "Would you like to remove this service from your bundle?"
                : "Select your preferred iCloud storage plan with Apple Music included"}
            </Text>
          </div>

          {!isAppleMusicInBundle && (
            <div className="options-container">
              {context?.pricing?.iCloudAppleMusicPricing?.map(
                (option, index) => (
                  <div
                    key={index}
                    className={`option-card glass-panel ${
                      selectedICloudOption === index ? "selected" : ""
                    }`}
                    onClick={() => setSelectedICloudOption(index)}
                  >
                    <Text
                      className="storage"
                      c={theme === "dark" ? Colors.White : Colors.BlackPrimary}
                    >
                      {option.storage}
                    </Text>
                    <div className="flex-row align-center justify-center">
                      <Text className="price" c={Colors.BluePrimary} fz={20}>
                        $
                        {getFinancialValueFromNumeric(
                          selectedPeriod === "30 Days"
                            ? option.price
                            : option.price *
                                getDiscountedValue(
                                  context.pricing?.possiblePeriodsCost.find(
                                    (p) => p.period === selectedPeriod
                                  )?.factor ?? 1,
                                  context?.pricing?.durationDiscounts?.find(
                                    (p) => p.period === selectedPeriod
                                  )?.discount ?? 0
                                )
                        )}
                      </Text>
                      <Text
                        ml={3}
                        fz="sm"
                        c={
                          theme === "dark"
                            ? Colors.GrayPrimary
                            : Colors.BlackPrimary
                        }
                      >
                        /{selectedPeriod}
                      </Text>
                    </div>
                    <Text
                      className="description"
                      c={
                        theme === "dark"
                          ? Colors.WhiteBlueTransparent
                          : Colors.BlackPrimary
                      }
                    >
                      {option.description ||
                        "Includes Apple Music subscription"}
                    </Text>
                  </div>
                )
              )}
            </div>
          )}

          <div className="modal-footer">
            <Button
              variant="outline"
              onClick={CloseAppleMusicModal}
              color="gray"
              radius="xl"
            >
              Cancel
            </Button>
            <Button
              color={
                isAppleMusicInBundle ? Colors.RedPrimary : Colors.GreenPrimary
              }
              disabled={!isAppleMusicInBundle && selectedICloudOption === null}
              onClick={() => {
                if (isAppleMusicInBundle) {
                  setSelectedServices((prevServices) =>
                    prevServices.filter((s) => s !== "iCloud - Apple Music")
                  );
                  setICloudStorage(null);
                } else if (selectedICloudOption !== null) {
                  setSelectedServices((prevServices) => [
                    ...prevServices,
                    "iCloud - Apple Music",
                  ]);
                  setICloudStorage(
                    context?.pricing?.iCloudAppleMusicPricing[
                      selectedICloudOption
                    ]?.storage ?? null
                  );
                }
                CloseAppleMusicModal();
              }}
              radius="xl"
            >
              {isAppleMusicInBundle ? "Remove from Bundle" : "Add to Bundle"}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        opened={isGoogleOneModalOpened}
        onClose={CloseGoogleOneModal}
        size="lg"
        centered
        withCloseButton={false}
        className={`service-bundle-modal ${theme}`}
        overlayProps={{
          blur: 8,
        }}
        transitionProps={{ transition: "fade", duration: 200 }}
      >
        <div className="modal-content">
          <div className="header">
            <Text
              className="title"
              c={theme === "dark" ? Colors.White : Colors.BlackPrimary}
            >
              {isGoogleOneInBundle
                ? "Remove Google One Storage"
                : "Choose Your Google One Storage"}
            </Text>
            <Text
              className="subtitle"
              c={
                theme === "dark"
                  ? Colors.WhiteBlueTransparent
                  : Colors.BluePrimary
              }
            >
              {isGoogleOneInBundle
                ? "Would you like to remove this service from your bundle?"
                : "Select your preferred Google One storage plan"}
            </Text>
          </div>

          {!isGoogleOneInBundle && (
            <div className="options-container">
              {context?.pricing?.googleOneStoragePricing?.map(
                (option, index) => (
                  <div
                    key={index}
                    className={`option-card glass-panel ${
                      selectedGoogleOneOption === index ? "selected" : ""
                    }`}
                    onClick={() => setSelectedGoogleOneOption(index)}
                  >
                    <Text
                      className="storage"
                      c={theme === "dark" ? Colors.White : Colors.BlackPrimary}
                    >
                      {option.storage}
                    </Text>
                    <div className="flex-row align-center justify-center">
                      <Text className="price" c={Colors.BluePrimary} fz={20}>
                        $
                        {getFinancialValueFromNumeric(
                          selectedPeriod === "30 Days"
                            ? option.price
                            : option.price *
                                getDiscountedValue(
                                  context.pricing?.possiblePeriodsCost.find(
                                    (p) => p.period === selectedPeriod
                                  )?.factor ?? 1,
                                  context?.pricing?.durationDiscounts?.find(
                                    (p) => p.period === selectedPeriod
                                  )?.discount ?? 0
                                )
                        )}
                      </Text>
                      <Text
                        ml={3}
                        fz="sm"
                        c={
                          theme === "dark"
                            ? Colors.GrayPrimary
                            : Colors.BlackPrimary
                        }
                      >
                        /{selectedPeriod}
                      </Text>
                    </div>
                    <Text
                      className="description"
                      c={
                        theme === "dark"
                          ? Colors.WhiteBlueTransparent
                          : Colors.BlackPrimary
                      }
                    >
                      {option.description || "Includes Google One subscription"}
                    </Text>
                  </div>
                )
              )}
            </div>
          )}

          <div className="modal-footer">
            <Button
              variant="outline"
              onClick={CloseGoogleOneModal}
              color="gray"
              radius="xl"
            >
              Cancel
            </Button>
            <Button
              color={
                isGoogleOneInBundle ? Colors.RedPrimary : Colors.GreenPrimary
              }
              disabled={
                !isGoogleOneInBundle && selectedGoogleOneOption === null
              }
              onClick={() => {
                if (isGoogleOneInBundle) {
                  setSelectedServices((prevServices) =>
                    prevServices.filter((s) => s !== "Google One")
                  );
                  setGoogleOneStorage(null);
                } else if (selectedGoogleOneOption !== null) {
                  setSelectedServices((prevServices) => [
                    ...prevServices,
                    "Google One",
                  ]);
                  setGoogleOneStorage(
                    context?.pricing?.googleOneStoragePricing[
                      selectedGoogleOneOption
                    ]?.storage ?? null
                  );
                }
                CloseGoogleOneModal();
              }}
              radius="xl"
            >
              {isGoogleOneInBundle ? "Remove from Bundle" : "Add to Bundle"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Cart placeholder no longer needed as we have sidebar */}
    </div>
  );
};

export default Services;
